import React, { useState, useRef, useEffect } from 'react';
import { Link, NavLink, useNavigate, useLocation } from 'react-router-dom';
import Logo from './Logo';
import CartIcon from './CartIcon';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import { selectCurrentUser, selectIsAuthenticated, logout } from '../store/slices/authSlice';
import authService from '../services/auth';
import './Header.css';

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const currentUser = useAppSelector(selectCurrentUser);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);

  const handleWhatsAppClick = () => {
    const phoneNumber = '2348143646922';
    const message = encodeURIComponent('Hello! I would like to get more information about your products and services.');
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${message}`;
    window.open(whatsappUrl, '_blank');
  };

  const handleLogout = () => {
    authService.logout();
    dispatch(logout());
    setDropdownOpen(false);
    navigate('/');
  };

  return (
    <header className="header">
      <div className="header-container">
        <Link to="/" className="logo-section">
          <Logo />
          <span className="logo-text">FM ULTRATECH FARMS</span>
        </Link>

        <nav className="nav-menu">
          <NavLink to="/" className={({ isActive }) => `nav-link${isActive ? ' nav-link--active' : ''}`} end>Home</NavLink>
          <NavLink to="/products" className={({ isActive }) => `nav-link${isActive ? ' nav-link--active' : ''}`}>Products</NavLink>
          <NavLink to="/services" className={({ isActive }) => `nav-link${isActive ? ' nav-link--active' : ''}`} end>Services</NavLink>
          <NavLink to="/about" className={({ isActive }) => `nav-link${isActive ? ' nav-link--active' : ''}`} end>About Us</NavLink>
          <NavLink to="/people" className={({ isActive }) => `nav-link${isActive ? ' nav-link--active' : ''}`} end>People Involved</NavLink>
          <NavLink to="/contact" className={({ isActive }) => `nav-link${isActive ? ' nav-link--active' : ''}`} end>Contact</NavLink>
        </nav>

        <div className="header-actions">
          <button className="whatsapp-btn" onClick={handleWhatsAppClick}>
            <svg className="whatsapp-icon" width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.372a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
            </svg>
            Whatsapp Us
          </button>
          <CartIcon />
          {isAuthenticated && currentUser ? (
            <div className="user-menu-wrapper" ref={dropdownRef}>
              <button
                type="button"
                className="icon-btn user-icon-btn"
                onClick={() => setDropdownOpen((prev) => !prev)}
                aria-expanded={dropdownOpen}
                aria-haspopup="true"
              >
                <svg className="user-icon" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                  <circle cx="12" cy="7" r="4"></circle>
                </svg>
              </button>
              {dropdownOpen && (
                <div className="user-dropdown">
                  <div className="user-dropdown-email">{currentUser.email}</div>
                  <Link to="/profile" className="user-dropdown-item" onClick={() => setDropdownOpen(false)}>
                    Profile
                  </Link>
                  <Link to="/my-orders" className="user-dropdown-item" onClick={() => setDropdownOpen(false)}>
                    My Orders
                  </Link>
                  <button type="button" className="user-dropdown-item user-dropdown-logout" onClick={handleLogout}>
                    Logout
                  </button>
                </div>
              )}
            </div>
          ) : (
            <Link to="/login" className="icon-btn">
              <svg className="user-icon" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                <circle cx="12" cy="7" r="4"></circle>
              </svg>
            </Link>
          )}
          
          <button 
            className="icon-btn hamburger-btn" 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle navigation menu"
            aria-expanded={mobileMenuOpen}
          >
            <svg className="hamburger-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              {mobileMenuOpen ? (
                <path d="M18 6L6 18M6 6l12 12" />
              ) : (
                <path d="M3 12h18M3 6h18M3 18h18" />
              )}
            </svg>
          </button>
        </div>
      </div>
      
      {/* Mobile Navigation Menu */}
      <div className={`mobile-nav ${mobileMenuOpen ? 'mobile-nav--open' : ''}`}>
        <nav className="mobile-nav-menu">
          <NavLink to="/" className={({ isActive }) => `mobile-nav-link${isActive ? ' mobile-nav-link--active' : ''}`} end onClick={() => setMobileMenuOpen(false)}>Home</NavLink>
          <NavLink to="/products" className={({ isActive }) => `mobile-nav-link${isActive ? ' mobile-nav-link--active' : ''}`} onClick={() => setMobileMenuOpen(false)}>Products</NavLink>
          <NavLink to="/services" className={({ isActive }) => `mobile-nav-link${isActive ? ' mobile-nav-link--active' : ''}`} end onClick={() => setMobileMenuOpen(false)}>Services</NavLink>
          <NavLink to="/about" className={({ isActive }) => `mobile-nav-link${isActive ? ' mobile-nav-link--active' : ''}`} end onClick={() => setMobileMenuOpen(false)}>About Us</NavLink>
          <NavLink to="/people" className={({ isActive }) => `mobile-nav-link${isActive ? ' mobile-nav-link--active' : ''}`} end onClick={() => setMobileMenuOpen(false)}>People Involved</NavLink>
          <NavLink to="/contact" className={({ isActive }) => `mobile-nav-link${isActive ? ' mobile-nav-link--active' : ''}`} end onClick={() => setMobileMenuOpen(false)}>Contact</NavLink>
        </nav>
      </div>
    </header>
  );
};

export default Header;
